import {
  Component,
  h,
  Prop,
  State,
  Element,
  Listen,
  Method,
  Event,
  EventEmitter,
} from '@stencil/core';
import { createUuid } from '../../../utils/functions/uuid';
import { isBreakpointEqualOrLarger, isBreakpointEqualOrSmaller } from '../../../utils';
import { BreadcrumbItem } from '../dc-ui-header-nav-breadcrumb/dc-ui-header-nav-breadcrumb';
import { DcUiHeaderFlyoutNavItem } from '../flyout-items/dc-ui-header-flyout-nav-item/dc-ui-header-flyout-nav-item';
import { FocusTrapHelper } from '../../../utils/functions/focusTrapHelper';

interface NavigationState {
  activeContainers: {
    level: string;
    parentId: string;
  }[];
  selectedPath: string[];
  currentLevel: number;
  breadcrumbItems: BreadcrumbItem[];
}

@Component({
  tag: 'dc-ui-header-main-nav-item',
  styleUrl: 'dc-ui-header-main-nav-item.scss',
  shadow: true,
})
export class DcUiHeaderMainNavItem {
  // Properties and State
  @Element() el!: HTMLElement;
  @Prop() label!: string;
  @State() isActive: boolean = false;
  @State() wasTriggeredByKeyboard: boolean = false;

  // Private members
  private readonly uuid: string = createUuid();
  private readonly STORAGE_KEY = 'nav-path';
  private breadcrumbElement: HTMLDcUiHeaderNavBreadcrumbElement;
  private maxEntries: number = 0;
  private instanceId: number;
  private mainHeaderElement: HTMLDcUiHeaderElement;
  private mainHeaderNavElement: HTMLDcUiHeaderMainNavElement;
  private focusTrapHelper: FocusTrapHelper;

  private mainNavItem: HTMLDivElement;
  private mainNavItemTrigger: HTMLButtonElement;

  // Add new private member
  private currentBreakpoint: 'mobile' | 'desktop' = this.getCurrentBreakpoint();

  // Add new private method
  private getCurrentBreakpoint(): 'mobile' | 'desktop' {
    return isBreakpointEqualOrLarger('xl') ? 'desktop' : 'mobile';
  }

  // Events
  @Event() dcFlyoutClose: EventEmitter<HTMLDcUiHeaderMainNavItemElement>;

  // Lifecycle Methods
  componentWillLoad() {
    // add a check to see if the component is inside a main nav component
    this.mainHeaderElement = this.el.closest('dc-ui-header');
    this.mainHeaderNavElement = this.el.closest('dc-ui-header-main-nav');
    if (!this.mainHeaderNavElement) {
      console.error(
        'dc-ui-header-main-nav-item must be used inside a dc-ui-header-main-nav component'
      );
      return;
    }

    const allNavItems = Array.from(
      this.mainHeaderNavElement.querySelectorAll('dc-ui-header-main-nav-item')
    );
    this.instanceId = allNavItems.indexOf(this.el as HTMLDcUiHeaderMainNavItemElement);
  }

  componentDidLoad() {
    this.organizeMenuHierarchy(this.el, null, {}, 0);
    this.focusTrapHelper = new FocusTrapHelper(this.el);
    this.addEventListenersToMenuItems();
    this.loadNavigationState();
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    const parentElement = this.el.parentElement;
    if (!parentElement) return;

    const containers = parentElement.querySelectorAll('[data-nav-level]');
    containers.forEach((container) => container.remove());

    sessionStorage.removeItem(`${this.STORAGE_KEY}-${this.instanceId}`);
    window.removeEventListener('resize', this.handleResize);
  }

  // Event Listeners
  @Listen('click', { target: 'window' })
  handleWindowClick(event: MouseEvent) {
    if (
      this.isActive &&
      isBreakpointEqualOrLarger('lg') &&
      !event.composedPath().includes(this.el)
    ) {
      this.closeFlyout();
    }
  }

  @Listen('keydown', { target: 'window' })
  handleKeyDown(event: KeyboardEvent) {
    if (this.isActive && event.key === 'Escape' && isBreakpointEqualOrLarger('lg')) {
      this.closeFlyout();
      this.mainNavItemTrigger.focus();
    }
  }

  @Listen('keydown')
  handleDocumentKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      const activeElement = document.activeElement;
      if (activeElement === this.el) {
        this.wasTriggeredByKeyboard = true;
      }
    }
  }

  @Listen('breadcrumbClick')
  handleBreadcrumbClick(event: CustomEvent) {
    const index = event.detail.index;

    // Mobile part
    if (isBreakpointEqualOrSmaller('xl')) {
      this.mobileMenuBack();
      return;
    }

    // Desktop part
    this.toggleAllMenuContainersAfterLevel(index);
  }

  // Public Methods
  @Method()
  async closeFlyout() {
    this.isActive = false;
    this.mainNavItem.classList.remove('is-active');
    this.mainNavItemTrigger.classList.remove('is-active');
    // Restore tabindex when closing
    this.mainNavItemTrigger.removeAttribute('tabindex');
    // Focus the trigger button when closing
    this.focusTrapHelper.deactivateFocusTrap();
  }

  private closeButtonHandler = () => {
    this.mainNavItemTrigger.focus();
    this.dcFlyoutClose.emit(this.el as HTMLDcUiHeaderMainNavItemElement);
    this.closeFlyout();
  };

  // Private Methods - Flyout Management
  private toggleFlyout = () => {
    if (this.isActive) {
      this.closeFlyout();
    } else {
      if (isBreakpointEqualOrLarger('xl')) {
        this.el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        setTimeout(() => {
          this.mainHeaderNavElement.updateButtonStates();
        }, 250);
      }
      this.openFlyout();
    }
  };

  private openFlyout() {
    this.isActive = true;
    this.mainNavItem.classList.add('is-active');
    this.mainNavItemTrigger.classList.add('is-active');
    this.mainNavItemTrigger.setAttribute('tabindex', '-1');

    const firstMenuContainer = this.el.querySelector('.menu-container');
    firstMenuContainer?.classList?.add('active');

    /* Mobile */
    if (isBreakpointEqualOrSmaller('xl')) {
      // Add the "Menu" root item to the breadcrumb
      this.breadcrumbElement?.addRootItem();
    }

    this.updateHiddenMenuContainers();

    // Focus the first menu item only if triggered by keyboard
    if (this.wasTriggeredByKeyboard) {
      const firstMenuItem = this.el.querySelector(
        '.menu-container .menu-item'
      ) as HTMLDcUiHeaderFlyoutTitleLinkElement;
      if (firstMenuItem) {
        firstMenuItem.focusElement();
      }
    }
    this.wasTriggeredByKeyboard = false;

    // Activate focus trap when opening the flyout
    this.focusTrapHelper.activateFocusTrap();
  }

  // Private Methods - Menu Organization
  private organizeMenuHierarchy(
    element: HTMLElement,
    parentId = null,
    groupedContainers = {},
    level = 0,
    path = ''
  ) {
    const children = Array.from(element.children);

    if (!groupedContainers[parentId]) {
      const groupContainer = document.createElement('ul');
      groupContainer.className = 'menu-container scrollbar-visible';
      groupContainer.setAttribute('data-parent-id', parentId);
      groupContainer.setAttribute('data-nav-level', level.toString());
      if (parentId === null && isBreakpointEqualOrLarger('xl'))
        groupContainer.classList.add('active');
      this.el.appendChild(groupContainer);
      groupedContainers[parentId] = groupContainer;
    }

    children.forEach((child, index) => {
      // Include instance ID in the item ID only at the start
      const id = path ? `${path}-${index}` : `nav${this.instanceId}-item-${index}`;
      child.setAttribute('data-id', id);
      child.classList.add('menu-item');
      groupedContainers[parentId].appendChild(child);

      if (
        child.children.length > 0 &&
        (child.tagName.toLowerCase() === 'dc-ui-header-flyout-nav-item' ||
          (child.hasAttribute('data-nesting') && child.getAttribute('data-nesting') !== 'false'))
      ) {
        this.organizeMenuHierarchy(child as HTMLElement, id, groupedContainers, level + 1, id);
      }
    });

    // After organizing all containers, find the maximum number of menu items
    const allContainers = this.el.querySelectorAll('.menu-container');
    allContainers.forEach((container) => {
      const menuItemCount = container.querySelectorAll('.menu-item').length;
      this.maxEntries = Math.max(this.maxEntries, menuItemCount);
    });

    // Set the CSS custom property
    this.el.style.setProperty('--max-entries', this.maxEntries.toString());
  }

  // Private Methods - Event Handling
  private addEventListenersToMenuItems() {
    // We need to add event listeners to the flyout nav items to handle the click events
    const menuItems = this.el.querySelectorAll('dc-ui-header-flyout-nav-item');
    menuItems.forEach((menuItem) => {
      menuItem.addEventListener('click', (event) => this.handleMenuItemClick(event));
    });

    // Add listeners for title links, regular links and anchor tags, to handle the session management
    const clickableElements = this.el.querySelectorAll(
      'dc-ui-header-flyout-title-link, dc-ui-header-flyout-nav-link, a'
    );
    clickableElements.forEach((element) => {
      element.addEventListener('click', () => {
        const sessionKey = `${this.STORAGE_KEY}-${this.instanceId}`;
        this.mainHeaderElement.manageSession(sessionKey);
      });
    });
  }

  private async handleMenuItemClick(event: Event) {
    const clickedMenuItem = event.target as HTMLElement & DcUiHeaderFlyoutNavItem;
    const menuDetails = this.getMenuDetails(clickedMenuItem);

    if (!menuDetails.nextLevelContainer) return;

    this.updateBreadcrumb(menuDetails);
    this.updateMenuContainers(menuDetails);
    this.updateSelectedStates(clickedMenuItem, menuDetails.currentLevel);
    this.updateNavigationLevel(menuDetails.navLevelToToggle);

    this.updateHiddenMenuContainers();
    this.saveNavigationState();

    // focus the first item in the new menu container
    setTimeout(() => {
      this.manageFocus(menuDetails.nextLevelContainer);
    }, 100);
  }

  private manageFocus(nextLevelContainer: HTMLElement) {
    const firstItem = nextLevelContainer.querySelector('.menu-item');
    if (firstItem) {
      (firstItem as HTMLElement & { focusElement: () => void }).focusElement();
    }
  }
  /**
   * Extracts and returns menu item details from a clicked menu element
   * @param menuItem - The HTML element representing the clicked menu item
   * @returns An object containing:
   *  - id: The data-id attribute value
   *  - label: The label attribute or text content
   *  - currentContainer: The parent menu container element
   *  - currentLevel: The navigation level number of current container
   *  - overviewTitle: The title of the overview
   *  - nextLevelContainer: The child menu container element
   *  - navLevelToToggle: The navigation level of the next container
   */
  private getMenuDetails(menuItem: HTMLElement & DcUiHeaderFlyoutNavItem) {
    const id = menuItem.dataset.id;
    const label = menuItem.label || menuItem.textContent;
    const currentContainer = menuItem.closest('.menu-container') as HTMLElement;
    const currentLevel = Number(currentContainer?.dataset.navLevel);
    const overviewLabel = currentContainer.querySelector('dc-ui-header-flyout-title-link').label;
    const nextLevelContainer = this.el.querySelector(
      `.menu-container[data-parent-id="${id}"]`
    ) as HTMLElement;
    const navLevelToToggle = nextLevelContainer?.dataset.navLevel;

    return {
      id,
      label,
      currentContainer,
      currentLevel,
      overviewLabel,
      nextLevelContainer,
      navLevelToToggle,
    };
  }

  private updateBreadcrumb(menuDetails: ReturnType<typeof this.getMenuDetails>) {
    if (!this.breadcrumbElement) return;

    const { currentLevel, overviewLabel, label } = menuDetails;

    if (isBreakpointEqualOrSmaller('xl')) {
      // on mobile we add the overview title as the breadcrumb item
      this.breadcrumbElement.addItem(overviewLabel, false, currentLevel);
      return;
    }

    // on desktop, on the first click we add the overview title as the breadcrumb item and the title as the normal item
    if (currentLevel === 0) {
      this.breadcrumbElement.addItem(overviewLabel, true, currentLevel - 1); // Add as root item
      this.breadcrumbElement.addItem(label, false, currentLevel); // Add normal item
    } else {
      this.breadcrumbElement.addItem(label, false, currentLevel); // Add normal item
    }
  }

  private updateMenuContainers(menuDetails: ReturnType<typeof this.getMenuDetails>) {
    const { navLevelToToggle, nextLevelContainer } = menuDetails;

    const activeContainers = this.el.querySelectorAll('.menu-container.active');
    activeContainers.forEach((container) => {
      const containerLevel = container.getAttribute('data-nav-level');
      if (containerLevel && Number(containerLevel) >= Number(navLevelToToggle)) {
        container.classList.remove('active');
      }
    });

    nextLevelContainer.classList.toggle('active');
  }

  private updateSelectedStates(clickedMenuItem: HTMLElement, currentLevel: number) {
    const currentAndHigherLevelItems = Array.from(
      this.el.querySelectorAll('.menu-container .menu-item')
    ).filter((menuItem) => {
      const containerLevel = menuItem.closest('.menu-container')?.getAttribute('data-nav-level');
      return containerLevel && Number(containerLevel) >= currentLevel;
    });

    currentAndHigherLevelItems.forEach((menuItem) => menuItem.removeAttribute('selected'));
    clickedMenuItem.setAttribute('selected', 'true');
  }

  private updateNavigationLevel(navLevelToToggle: string) {
    const navLevelNumber = Number(navLevelToToggle) + 1;
    this.el.style.setProperty('--navigation-columns-current', navLevelNumber.toString());
  }

  // Private Methods - State Management
  private async saveNavigationState() {
    const activeMenuContainers = Array.from(this.el.querySelectorAll('.menu-container.active'));
    const selectedItems = Array.from(this.el.querySelectorAll('.menu-item[selected]'));
    const currentLevel = Number(this.el.style.getPropertyValue('--navigation-columns-current'));

    // Get breadcrumb items using the new export method
    const breadcrumbItems = this.breadcrumbElement
      ? await this.breadcrumbElement.exportState()
      : [];

    const state: NavigationState = {
      activeContainers: activeMenuContainers.map((container) => ({
        level: container.getAttribute('data-nav-level'),
        parentId: container.getAttribute('data-parent-id'),
      })),
      selectedPath: selectedItems.map((item) => item.getAttribute('data-id')),
      currentLevel: currentLevel,
      breadcrumbItems,
    };

    sessionStorage.setItem(`${this.STORAGE_KEY}-${this.instanceId}`, JSON.stringify(state));
  }

  private async loadNavigationState() {
    const savedState = sessionStorage.getItem(`${this.STORAGE_KEY}-${this.instanceId}`);
    if (!savedState) return;

    const state = JSON.parse(savedState);

    // Set the main flyout active state if there are any active containers
    if (state.activeContainers.length > 0 && isBreakpointEqualOrSmaller('xl')) {
      this.isActive = true;
    }

    // Restore active containers using both level and parent-id
    state.activeContainers.forEach(({ level, parentId }) => {
      const container = this.el.querySelector(
        `.menu-container[data-nav-level="${level}"][data-parent-id="${parentId}"]`
      );
      if (container) {
        container.classList.add('active');
      }
    });

    // Restore selected items
    state.selectedPath.forEach((itemId) => {
      const item = this.el.querySelector(`[data-id="${itemId}"]`);
      if (item) {
        item.setAttribute('selected', 'true');
      }
    });

    if (state.currentLevel) {
      this.el.style.setProperty('--navigation-columns-current', state.currentLevel.toString());
    }

    if (this.breadcrumbElement && state.breadcrumbItems) {
      await this.breadcrumbElement.importState(state.breadcrumbItems);
    }

    // For mobile: ensure the first container is active if we have an active state
    if (isBreakpointEqualOrSmaller('xl') && this.isActive) {
      const firstMenuContainer = this.el.querySelector('.menu-container');
      firstMenuContainer?.classList?.add('active');
      this.mainNavItem.classList.add('is-active');
      this.mainNavItemTrigger.classList.add('is-active');
    }
  }

  // Private Methods - UI Updates
  private updateHiddenMenuContainers() {
    // Only run this on desktop
    if (!isBreakpointEqualOrLarger('xl')) return;

    const activeMenuContainers = Array.from(this.el.querySelectorAll('.menu-container.active'));
    const currentLevel =
      Number(this.el.style.getPropertyValue('--navigation-columns-current')) || 0;
    const maxVisibleColumns = 3;

    // Calculate which containers should be hidden
    activeMenuContainers.forEach((container: HTMLElement) => {
      const containerLevel = Number(container.getAttribute('data-nav-level'));
      const isHidden = containerLevel < currentLevel - maxVisibleColumns;

      Array.from(container.children).forEach((child: HTMLElement) => {
        if (isHidden) {
          child.setAttribute('tabindex', '-1');
          child.setAttribute('aria-hidden', 'true');
        } else {
          child.removeAttribute('tabindex');
          child.removeAttribute('aria-hidden');
        }
      });
    });
  }

  private toggleAllMenuContainersAfterLevel(level: number) {
    const menuContainers = this.el.querySelectorAll('.menu-container');
    const activeMenuContainers = this.el.querySelectorAll('.menu-container.active');

    if (level === -1) {
      this.handleRootLevelReset(menuContainers);
      return;
    }

    if (level === 0) {
      this.handleSecondLevelReset(activeMenuContainers);
      return;
    }

    this.handleOtherLevelsReset(level, activeMenuContainers);
  }

  private handleRootLevelReset(menuContainers: NodeListOf<Element>) {
    // Reset containers
    menuContainers.forEach((container) => {
      container.classList.toggle('active', container.getAttribute('data-nav-level') === '0');
    });

    // Reset menu items
    this.el.querySelectorAll('.menu-item').forEach((item) => item.removeAttribute('selected'));

    // Reset navigation and breadcrumb
    this.el.style.setProperty('--navigation-columns-current', '1');
    this.breadcrumbElement?.removeAllItemsFrom(0);

    this.updateHiddenMenuContainers();
    this.saveNavigationState();
  }

  private handleSecondLevelReset(activeMenuContainers: NodeListOf<Element>) {
    activeMenuContainers.forEach((container) => {
      const containerLevel = Number(container.getAttribute('data-nav-level'));

      // Remove active class from higher level containers
      if (containerLevel > 1) {
        container.classList.remove('active');
      }

      // Clear selected states for level 1 and above
      if (containerLevel > 0) {
        container
          .querySelectorAll('.menu-item')
          .forEach((item) => item.removeAttribute('selected'));
      }
    });

    this.el.style.setProperty('--navigation-columns-current', '1');
    this.breadcrumbElement?.removeAllItemsFrom(2);
  }

  private handleOtherLevelsReset(level: number, activeMenuContainers: NodeListOf<Element>) {
    const navLevelNumber = level + 2;
    this.el.style.setProperty('--navigation-columns-current', navLevelNumber.toString());

    activeMenuContainers.forEach((container) => {
      const containerLevel = Number(container.getAttribute('data-nav-level'));

      if (containerLevel > level + 1) {
        container.classList.remove('active');
      }

      if (containerLevel > level) {
        container
          .querySelectorAll('.menu-item')
          .forEach((item) => item.removeAttribute('selected'));
      }
    });

    this.breadcrumbElement?.removeAllItemsFrom(level + 2);
    this.updateHiddenMenuContainers();
    this.saveNavigationState();
  }

  private resetMenuContainerAndItems() {
    // 1. remove selected state from all menu items
    const menuItems = this.el.querySelectorAll('.menu-item');
    menuItems.forEach((menuItem) => {
      menuItem.removeAttribute('selected');
    });

    // 2. remove active class from all menu containers
    const menuContainers = this.el.querySelectorAll('.menu-container');
    menuContainers.forEach((menuContainer) => {
      menuContainer.classList.remove('active');
    });
  }

  private mobileMenuBack() {
    // Get all active containers
    const activeContainers = Array.from(this.el.querySelectorAll('.menu-container.active'));

    if (activeContainers.length <= 1) {
      // If we're at the first level, close the flyout completely
      this.closeFlyout();
      this.resetMenuContainerAndItems();
      return;
    }

    // Get the last active container
    const lastActiveContainer = activeContainers[activeContainers.length - 1];
    const newActiveContainer = activeContainers[activeContainers.length - 2];
    const currentLevel = Number(lastActiveContainer.getAttribute('data-nav-level'));

    // Remove active class from the last container
    lastActiveContainer.classList.remove('active');

    // Remove selected state from items in the last container
    const menuItems = lastActiveContainer.querySelectorAll('.menu-item');
    menuItems.forEach((menuItem) => {
      menuItem.removeAttribute('selected');
    });

    // Remove selected state from items in the new active container
    const newActiveContainerMenuItems = newActiveContainer.querySelectorAll('.menu-item');
    newActiveContainerMenuItems.forEach((menuItem) => {
      menuItem.removeAttribute('selected');
    });

    // Update the navigation level
    const newLevel = currentLevel;
    this.el.style.setProperty('--navigation-columns-current', newLevel.toString());

    // Remove the last breadcrumb item
    if (this.breadcrumbElement) {
      this.breadcrumbElement.removeAllItemsFrom(currentLevel);
    }

    // Save the new navigation state
    this.saveNavigationState();
  }

  // Add new resize handler
  private handleResize = () => {
    const newBreakpoint = this.getCurrentBreakpoint();
    if (this.currentBreakpoint !== newBreakpoint) {
      this.currentBreakpoint = newBreakpoint;
      this.resetNavigation();
    }
  };

  private resetNavigation() {
    this.isActive = false;
    this.resetMenuContainerAndItems();
    if (this.breadcrumbElement) {
      this.breadcrumbElement.clear();
    }
    this.el.style.setProperty('--navigation-columns-current', '1');
    this.saveNavigationState();

    // Reset UI elements
    this.mainNavItem?.classList.remove('is-active');
    this.mainNavItemTrigger?.classList.remove('is-active');
  }

  // Render Method
  render() {
    const closeButton = (
      <dc-ui-button
        autofocus
        variant="link"
        icon="dc_close"
        hideLabel={true}
        id="close-icon"
        size="small"
        onClick={() => this.closeButtonHandler()}>
        Close
      </dc-ui-button>
    );

    return (
      <div
        ref={(el) => (this.mainNavItem = el)}
        data-nav-item-id={this.uuid}
        class="main-nav-item">
        <button
          ref={(el) => (this.mainNavItemTrigger = el)}
          onClick={this.toggleFlyout}
          class="main-nav-item-trigger">
          <span class="main-nav-item-title">{this.label}</span>
          <dc-ui-icon
            color="darkBlue02"
            name="dc_forward_right"
            size="tiny"></dc-ui-icon>
        </button>
        <div class="main-nav-item-content">
          <div class="navigation-body">
            <dc-ui-header-nav-breadcrumb
              ref={(el) => (this.breadcrumbElement = el)}></dc-ui-header-nav-breadcrumb>
            <div class="navigation-rows-wrapper">
              <slot />
            </div>
            {closeButton}
          </div>
        </div>
      </div>
    );
  }
}
